import { createContext, useContext, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { toast } from "sonner";

const PaymentContext = createContext(null);

export const usePayment = () => {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error("usePayment must be used within PaymentProvider");
  return ctx;
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export function PaymentProvider({ children }) {
  const busyRef = useRef(false);

  const buyArtwork = useCallback(async (artwork, { onEnquireFallback } = {}) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const done = () => { busyRef.current = false; };

    try {
      // 1. Check if payments are configured
      const cfg = await axios.get(`${API}/payments/config`);
      if (!cfg.data?.enabled) {
        toast.info("Online payment isn't set up yet \u2014 please send an enquiry instead.");
        if (onEnquireFallback) onEnquireFallback();
        return done();
      }

      // 2. Create order on backend
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        artwork_slug: artwork.slug,
      });
      const { order_id, amount, currency, key_id, artwork_title } = orderRes.data;

      // 3. Load Razorpay checkout
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Couldn't load the payment window. Please try again.");
        return done();
      }

      // 4. Open checkout
      const rzp = new window.Razorpay({
        key: key_id,
        amount,
        currency,
        name: "The Art Lounge Nirvana",
        description: artwork_title,
        image: "/logo.png",
        order_id,
        theme: { color: "#C85A17" },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`Payment successful \u2014 "${artwork_title}" is yours! The studio will be in touch about delivery.`);
          } catch (e) {
            toast.error("Payment received but verification failed. Please contact the studio.");
          } finally {
            done();
          }
        },
        modal: {
          ondismiss: () => {
            toast("Payment cancelled.");
            done();
          },
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        done();
      });
      rzp.open();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong starting the payment.";
      toast.error(msg);
      done();
    }
  }, []);

  return (
    <PaymentContext.Provider value={{ buyArtwork }}>
      {children}
    </PaymentContext.Provider>
  );
}
